// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import placeOrderForApproval from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/PlaceOrderForApproval";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { connect, ResolveThunks } from "react-redux";
import React, { useState } from "react";
import OrderActions from "@store/Library/Components/Toolkit/Order/OrderActions";
import OrderStickyAction from "@store/Library/Components/Toolkit/Order/OrderStickyAction";
import SignInTermsAndConditions from "@store/Library/Components/SignIn/SignInTermsAndConditions";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

const enum fields {
    enableTerms = "enableTerms",
    termsTitle = "termsTitle",
    termsLinkText = "termsLinkText",
    termsCheckboxLabel = "termsCheckboxLabel",
    termsContent = "termsContent",
    termsHideLinkText = "termsHideLinkText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.enableTerms]: boolean;
        [fields.termsTitle]: string;
        [fields.termsLinkText]: string;
        [fields.termsCheckboxLabel]: string;
        [fields.termsContent]: string;
        [fields.termsHideLinkText]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading } = cartState;
    return {
        cart,
        isLoading,
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        showPlaceOrderButton: canPlaceOrder(cart),
        showSubmitForApprovalOrderButton: canSubmitForApprovalOrder(cart),
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    setIsPlacingOrder,
    placeOrderForApproval,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OnePageCheckoutPlaceOrderButtonStyles {
    placeOrderButtonWrapper?: InjectableCss;
    placeOrderButton?: ButtonPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
}

export const placeOrderButtonStyles: OnePageCheckoutPlaceOrderButtonStyles = {
    placeOrderButtonWrapper: {
        css: css`
            width: 100%;
        `,
    },
    placeOrderButton: {
        sizeVariant: "large",
    },
};

const styles = placeOrderButtonStyles;

const OnePageCheckoutPlaceOrderButton = ({
    cart,
    isLoading,
    isPlacingOrder,
    showPlaceOrderButton,
    showSubmitForApprovalOrderButton,
    orderApprovalDetailsPageUrl,
    history,
    fields,
    setIsPlacingOrder,
    placeOrderForApproval,
}: Props) => {
    const [termsAccepted, setTermsAccepted] = useState(false);

    if (!cart) {
        return null;
    }

    const placeOrderClickHandler = () => {
        setIsPlacingOrder({ isPlacingOrder: true });
    };

    const submitForApprovalOrderClickHandler = () => {
        placeOrderForApproval({
            onSuccess: (cartId: string) => {
                if (orderApprovalDetailsPageUrl) {
                    history.push(`${orderApprovalDetailsPageUrl}?cartId=${cartId}`);
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart.id) {
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
        });
    };

    const isOverLimit = cart.properties.isOverOnlinePurchaseLimit === "true";
    const onAcceptedChange = (value: boolean) => {
        setTermsAccepted(value);
    };

    return showPlaceOrderButton || showSubmitForApprovalOrderButton ? (
        <OrderActions>
            <OrderStickyAction>
                {showPlaceOrderButton && (
                    <StyledWrapper {...styles.placeOrderButtonWrapper}>
                        <SignInTermsAndConditions
                            enabled={fields.enableTerms}
                            title={fields.termsTitle}
                            linkText={fields.termsLinkText}
                            hideLinkText={fields.termsHideLinkText}
                            checkboxLabel={fields.termsCheckboxLabel}
                            contentHtml={fields.termsContent}
                            accepted={termsAccepted}
                            onAcceptedChange={onAcceptedChange}
                        />
                        <Button
                            {...styles.placeOrderButton}
                            disabled={
                                isLoading || isPlacingOrder || isOverLimit || (fields.enableTerms && !termsAccepted)
                            }
                            onClick={placeOrderClickHandler}
                            data-test-selector="checkoutReviewAndSubmit_placeOrder"
                        >
                            {translate("Place Order")}
                        </Button>
                    </StyledWrapper>
                )}
                {showSubmitForApprovalOrderButton && (
                    <Button
                        {...styles.submitForApprovalButton}
                        disabled={isLoading || isPlacingOrder}
                        onClick={submitForApprovalOrderClickHandler}
                        data-test-selector="checkoutReviewAndSubmit_submitForApproval"
                    >
                        {translate("Submit For Approval")}
                    </Button>
                )}
            </OrderStickyAction>
        </OrderActions>
    ) : null;
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutPlaceOrderButton)),
    definition: {
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        displayName: "One Page Checkout Place Order Button",
        fieldDefinitions: [
            {
                name: fields.enableTerms,
                displayName: "Enable Terms & Conditions",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                fieldType: "Contextual",
                name: fields.termsTitle,
                editorTemplate: "TextField",
                displayName: "Terms Title",
                defaultValue: "Terms & Conditions",
                sortOrder: 3,
                isVisible: x => x.fields.enableTerms,
            },
            {
                fieldType: "Contextual",
                name: fields.termsLinkText,
                editorTemplate: "TextField",
                displayName: "Terms Link Text",
                defaultValue: "Open and read all Terms & Conditions",
                sortOrder: 4,
                isVisible: x => x.fields.enableTerms,
            },
            {
                fieldType: "Contextual",
                name: fields.termsCheckboxLabel,
                editorTemplate: "TextField",
                displayName: "Terms Checkbox Label",
                defaultValue: "I have read and agree to the above terms. *",
                sortOrder: 5,
                isVisible: x => x.fields.enableTerms,
            },
            {
                fieldType: "Contextual",
                name: fields.termsContent,
                editorTemplate: "RichTextField",
                displayName: "Terms Content",
                defaultValue:
                    "<p><strong>Purchase Order Acknowledgement</strong></p><p>By submitting this order, you acknowledge that you are authorized to place orders on behalf of the distributor and you are placing a firm purchase order, subject to acceptance by FLSmidth.</p><p>This order is governed by the terms and conditions of your current Distributor Agreement (or other applicable commercial agreement) with FLSmidth. In the event that such agreement has expired or is otherwise not in effect, FLSmidth's prevailing Terms and Conditions of Sale shall apply.</p><p>Prices, product availability, and delivery schedules are subject to order acceptance and confirmation by FLSmidth. Applicable taxes, duties, freight charges, and other charges will be reflected in the final invoice, where applicable.</p><p>Export control and sanctions compliance requirements apply where applicable.</p>",
                extendedConfig: { height: 220 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 6,
                isVisible: x => x.fields.enableTerms,
            },
            {
                fieldType: "Contextual",
                name: fields.termsHideLinkText,
                editorTemplate: "TextField",
                displayName: "Terms Hide Link Text",
                defaultValue: "Hide Terms & Conditions",
                sortOrder: 7,
                isVisible: x => x.fields.enableTerms,
            },
        ],
    },
};

export default widgetModule;
